import React from 'react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import { isBrowser, isTablet } from 'react-device-detect'

import { HelpLink } from '../../../../../../components/generic'
import {
  EMARInitialAssessmentEmptyState,
  EMARScreenEmptyState,
} from '../../components'
import DisabledPDFButton from '../components/DisabledPDFButton'
import PdfExport from '../../../../../../components/pdf/PdfExport'
import MARChart from '../../EMARChart/EMARChart'
import { ButtonOLD, PageHeader } from '@lmc/ui-kit'
import Glyph from '../../../../../../LEGACY/Glyph'
import { history } from '../../../../../../store'

const getPDFUrl = (props) => {
  const { endDate, resident, startDate } = props
  return encodeURIComponent(
    `residents/${resident.id}/pdf/emar?printpdf=1&startDate=${dayjs(
      startDate,
    ).toISOString()}&endDate=${dayjs(endDate).toISOString()}`,
  )
}

/**
@description Renders the MAR chart for a resident

@param {array} chartData. The data to be rendered in the chart. Is a collection
of medication objects.

@param {function} fetchData. Fetches data from Redux.

@param {function} fetchLogs. Fetches logs from Redux.

@param {function} openModal. Opens the Add Medication modal.

@param {object} params. The parameters for the screen. Contains the resident id.

@param {object} resident. Object containing the resident's data, e.g. their
name.
* */

export default function EMARResidentMARChartScreen(props) {
  const {
    chartData = [],
    endDate,
    home,
    openModal,
    openOnboardingModal,
    resident,
    residentName,
    setEndDate,
    setStartDate,
    startDate,
    user,
    router,
  } = props

  const url = `/api/services/pdf/export?url=${getPDFUrl(props)}`

  const dateIntervalLength = endDate.diff(startDate, 'days')
  const hideScroll = dateIntervalLength < 15

  const isInitialAssessment =
    resident.status === 'pre-assessment' || resident.isActiveInitialAssessment

  return (
    <div style={styles.wrapper}>
      <PageHeader
        title={`${residentName}'s eMar`}
        sticky
        onBack={
          !isDesktop
            ? () => history.replace?.(`/residents/${router.params.id}/profile`)
            : undefined
        }
      >
        <div style={styles.headerBtnContainer}>
          {!isInitialAssessment && !!chartData?.length && (
            <div>
              {dateIntervalLength < 28 ? (
                <PdfExport
                  pdfTitle={`${residentName}'s MAR chart`}
                  url={url}
                  text={isDesktop ? 'Download PDF' : 'PDF'}
                />
              ) : (
                <DisabledPDFButton />
              )}
            </div>
          )}
          {user.medicationPermission && !isInitialAssessment && (
            <ButtonOLD
              id="add-medication-button"
              onClick={() => openModal(resident.id)}
              style={styles.addMedicationButton}
              type="primary"
              disabled={
                home &&
                !home.features?.emarOnboarded?.enabled &&
                !chartData?.length &&
                !isInitialAssessment
              }
              icon={<Glyph name="plus" />}
            >
              Add Medication
            </ButtonOLD>
          )}
        </div>
      </PageHeader>
      {isInitialAssessment ? (
        <EMARInitialAssessmentEmptyState
          preferredPeopleNominative={home.preferredPeopleNominative}
          resident={resident}
        />
      ) : chartData?.length ? (
        <MARChart
          endDate={endDate}
          medications={chartData}
          rowDisplaySettings={{
            isMedLinkActive: true,
          }}
          hideScroll={hideScroll}
          setEndDate={setEndDate}
          setStartDate={setStartDate}
          startDate={startDate}
          todayDate={dayjs()}
        />
      ) : (
        <>
          <EMARScreenEmptyState
            openOnboardingModal={openOnboardingModal}
            emarOnboarded={home && home.features?.emarOnboarded?.enabled}
            resident={resident}
          />
        </>
      )}
      {/* <div style={styles.footer}>
        <HelpLink supportLink="https://support.logmycare.co.uk/en/collections/2749236-emar" />
      </div> */}
    </div>
  )
}

const isDesktop = isBrowser || isTablet

EMARResidentMARChartScreen.propTypes = {
  chartData: PropTypes.array.isRequired,
  endDate: PropTypes.object.isRequired,
  home: PropTypes.object.isRequired,
  openModal: PropTypes.func.isRequired,
  openOnboardingModal: PropTypes.func.isRequired,
  resident: PropTypes.object.isRequired,
  residentName: PropTypes.string.isRequired,
  setEndDate: PropTypes.func.isRequired,
  setStartDate: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
}

const styles = {
  addMedicationButton: {
    marginLeft: 10,
  },
  buttonContainer: {
    marginRight: 10,
  },
  headerSection: { marginBottom: 12, marginTop: 24 },
  headerBtnContainer: { display: 'flex', flexDirection: 'row' },
  footer: {
    display: 'flex',
    marginTop: 24,
  },
  wrapper: {
    padding: '0px 0px 24px',
    margin: '0px auto',
    maxWidth: 1080,
  },
}
